import { useState } from "react";
import {
  Box,
  ChevronDownIcon,
  ChevronRightIcon,
  Column,
  ErrorIcon,
  Paragraph,
  Row,
  Spinner,
  SuccessIcon,
  Text,
  TextWithTooltip,
} from "@hightouch/htui";
import type { DisclaimerSpec } from "@hightouch/lib/ad-studio/disclaimer/types";
import type { VideoExportSelection } from "src/pages/ad-studio/briefs/hooks/bulk-video-download-targets";
import type {
  BulkVideoDownloadPhase,
  BulkVideoDownloadState,
} from "src/pages/ad-studio/briefs/hooks/use-bulk-video-download";

export type DownloadItemStatus = "pending" | "running" | "complete" | "failed";

/** One row in a progress section: a static ad or a video render. */
export type DownloadProgressItem = {
  key: string;
  name: string;
  status: DownloadItemStatus;
};

export type StaticDownloadPhase =
  | "idle"
  | "building"
  | "zipping"
  | "done"
  | "error";

export type StaticDownloadProgress = {
  phase: StaticDownloadPhase;
  items: DownloadProgressItem[];
  /** Files in the archive, known once it's built. */
  fileCount: number;
  /** Per-file notices: an oversized HTML5 bundle, a PSD that failed. */
  warnings: string[];
  /** Set when the whole download fails, as opposed to individual ads failing. */
  error: string | null;
};

/** A batch of renders the dialog drives through the video export pipeline. */
export type VideoDownloadJob = {
  selection: VideoExportSelection;
  spec: DisclaimerSpec;
};

export type DownloadProgressBodyProps = {
  staticProgress: StaticDownloadProgress;
  videoJob: VideoDownloadJob | null;
  /** The render pipeline's state, owned by the dialog frame. */
  video: BulkVideoDownloadState;
};

/**
 * Progress for a download: an Images section for the client-side zip and a
 * Videos section for the server-side renders, each present only when the
 * selection had that kind of ad.
 */
export const DownloadProgressBody = ({
  staticProgress,
  videoJob,
  video,
}: DownloadProgressBodyProps) => {
  const isStaticRunning =
    staticProgress.phase === "building" || staticProgress.phase === "zipping";
  const isVideoRunning =
    video.phase === "rendering" || video.phase === "bundling";

  return (
    <Column gap={4}>
      {/* Renders take minutes and closing abandons them; a zip is done in seconds. */}
      {isVideoRunning && (
        <Paragraph color="text.tertiary">
          Keep this open until exporting is complete.
        </Paragraph>
      )}

      {staticProgress.phase !== "idle" && (
        <ProgressSection
          title="Images"
          items={staticProgress.items}
          isRunning={isStaticRunning}
          statusMessage={statusMessage({
            phase: staticProgress.phase,
            error: staticProgress.error,
            fileCount: staticProgress.fileCount,
          })}
          notes={staticProgress.warnings}
        />
      )}

      {videoJob && (
        <ProgressSection
          title="Videos"
          items={video.items.map((item) => ({
            key: item.target.key,
            name: item.target.name,
            status: item.status === "rendering" ? "running" : item.status,
          }))}
          isRunning={isVideoRunning}
          statusMessage={statusMessage({
            phase: video.phase,
            error: video.error,
            isVideo: true,
          })}
          notes={videoNotes(video, videoJob.selection.unexportableCount)}
        />
      )}
    </Column>
  );
};

/** Video renders run for minutes, so `isVideo` adds a wait notice. */
function statusMessage(args: {
  phase: StaticDownloadPhase | BulkVideoDownloadPhase;
  error: string | null;
  isVideo?: boolean;
  /** Omitted for videos, which always arrive as a server-built archive. */
  fileCount?: number;
}): string {
  const { phase, error, isVideo = false, fileCount } = args;
  switch (phase) {
    case "done":
      // A lone file skips the archive, so the copy can't promise a ZIP.
      return fileCount === 1
        ? "Export completed. File downloaded."
        : "Export completed. ZIP file downloaded.";
    case "error":
      return (
        error ??
        "Something went wrong. Try again, or contact support if it keeps happening."
      );
    case "idle":
      return "Getting started.";
    default:
      return isVideo
        ? "Preparing your download. This can take a few minutes."
        : "Preparing your download.";
  }
}

/** Selected ads that never made it into the archive, and why. */
function videoNotes(
  video: BulkVideoDownloadState,
  unexportableCount: number,
): string[] {
  const notes: string[] = [];
  if (video.failedCount > 0 && video.phase !== "error") {
    notes.push(
      video.failedCount === 1
        ? "1 video couldn't be rendered and isn't included."
        : `${video.failedCount} videos couldn't be rendered and aren't included.`,
    );
  }
  if (video.skippedCount > 0) {
    notes.push(
      video.skippedCount === 1
        ? "1 video couldn't be added to the archive. See FAILED.txt inside the download."
        : `${video.skippedCount} videos couldn't be added to the archive. See FAILED.txt inside the download.`,
    );
  }
  if (unexportableCount > 0) {
    notes.push(
      unexportableCount === 1
        ? "1 selected ad has no saved version to export."
        : `${unexportableCount} selected ads have no saved version to export.`,
    );
  }
  return notes;
}

type ProgressSectionProps = {
  title: string;
  items: DownloadProgressItem[];
  isRunning: boolean;
  statusMessage: string;
  notes: string[];
};

/** A card per asset kind. The row list collapses so a long batch stays scannable. */
const ProgressSection = ({
  title,
  items,
  isRunning,
  statusMessage,
  notes,
}: ProgressSectionProps) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const completeCount = items.filter(
    (item) => item.status === "complete",
  ).length;

  return (
    <Column
      borderWidth="hairline"
      borderColor="base.border"
      borderRadius="md"
      overflow="hidden"
    >
      <Row
        as="button"
        type="button"
        align="center"
        justify="space-between"
        gap={2}
        width="100%"
        px={3}
        py={2}
        aria-expanded={isExpanded}
        cursor="pointer"
        bg="transparent"
        border="none"
        textAlign="left"
        _hover={{ bg: "base.lightBackground" }}
        _focusVisible={{ boxShadow: "outline" }}
        onClick={() => setIsExpanded((expanded) => !expanded)}
      >
        <Row align="center" gap={2} minWidth={0}>
          <Text fontWeight="medium">{title}</Text>
          <Text size="sm" color="text.secondary">
            {completeCount} of {items.length}
          </Text>
        </Row>
        <Row align="center" gap={2} flexShrink={0}>
          {isRunning && <Spinner size="sm" />}
          <Box
            as={isExpanded ? ChevronDownIcon : ChevronRightIcon}
            boxSize={4}
            color="text.secondary"
          />
        </Row>
      </Row>
      <Column gap={2} px={3} pb={3}>
        <Paragraph color="text.secondary">{statusMessage}</Paragraph>
        {isExpanded && items.length > 0 && (
          <Column gap={2} maxHeight={64} overflowY="auto">
            {items.map((item) => (
              <ItemRow key={item.key} item={item} />
            ))}
          </Column>
        )}
        {notes.map((note) => (
          <Text key={note} color="text.warning">
            {note}
          </Text>
        ))}
      </Column>
    </Column>
  );
};

const ItemRow = ({ item }: { item: DownloadProgressItem }) => (
  <Row align="center" gap={2} justify="space-between">
    <TextWithTooltip size="sm">{item.name}</TextWithTooltip>
    {/* Fixed slot so a landing icon can't reflow the truncated name beside it. */}
    <Row align="center" justify="center" flexShrink={0} boxSize={4}>
      <ItemStatus status={item.status} />
    </Row>
  </Row>
);

/** In-flight rows stay empty: the section header's spinner covers the list. */
const ItemStatus = ({ status }: { status: DownloadItemStatus }) => {
  switch (status) {
    case "complete":
      return <SuccessIcon color="success.base" />;
    case "failed":
      return <ErrorIcon color="danger.base" />;
    default:
      return null;
  }
};
